import React from "react";
import styled from "styled-components";
import { FiRefreshCcw, FiTrash2, FiCopy } from "react-icons/fi";
import { FaStar } from "react-icons/fa";
import { theme } from "../../styles/theme";
import { Button } from "../ui/Button";
import { StatusBadge } from "../ui/StatusBadge";

const ProjectsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

const ProjectSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const ProjectHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${theme.spacing.md};
  flex-wrap: wrap;
  padding: ${theme.spacing.xs} 0;
  border-bottom: ${theme.borders.base};
`;

const HeaderDetails = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const CoverThumb = styled.img`
  width: 64px;
  height: 64px;
  border-radius: ${theme.borderRadius.md};
  border: ${theme.borders.base};
  object-fit: cover;
  background: ${theme.colors.background.tertiary};
`;

const CoverPlaceholder = styled.div`
  width: 64px;
  height: 64px;
  border-radius: ${theme.borderRadius.md};
  border: ${theme.borders.base};
  background: ${theme.colors.background.tertiary};
`;

const VersionsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
`;

const VersionCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  flex: 1 1 clamp(220px, 40vw, 320px);
  max-width: 340px;
  padding: ${theme.spacing.sm};
  border: ${theme.borders.base};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.canvas};
  align-items: center;
`;

const Thumb = styled.img`
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
  border-radius: ${theme.borderRadius.md};
  border: ${theme.borders.base};
  background: ${theme.colors.background.tertiary};
`;

const Info = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing["2xs"]};
  align-items: flex-start;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing["2xs"]};
  width: 100%;
  align-items: stretch;
`;

function ProjectsPanel({
  projects,
  onRemix,
  onRender,
  onDeleteVersion,
  onDeleteProject,
  onRetry,
  onClone,
}) {
  if (!projects.length) {
    return <span style={{ color: theme.colors.text.muted }}>No projects yet.</span>;
  }

  return (
    <ProjectsStack>
      {projects.map((project) => {
        const versions = project.versions || [];
        const sortedVersions = [...versions].sort((a, b) => (a.created_at || 0) - (b.created_at || 0));
        const firstVersion = sortedVersions[0];
        
        const cover =
          project.seed_preview_url ||
          firstVersion?.source_image_url ||
          project.cover_image_url ||
          versions[0]?.current_image_url;

        return (
          <ProjectSection key={project.id}>
            <ProjectHeader>
              <HeaderDetails>
                {cover ? <CoverThumb src={cover} alt="Project" /> : <CoverPlaceholder />}
                <div
                  style={{
                    color: theme.colors.text.secondary,
                    fontSize: theme.typography.fontSize.sm,
                  }}
                >
                  {versions.length} version{versions.length === 1 ? "" : "s"}
                  {project.seed_starred ? (
                    <FaStar style={{ marginLeft: theme.spacing["2xs"], color: "#f5c518" }} />
                  ) : null}
                </div>
              </HeaderDetails>
              <div style={{ display: "flex", flexDirection: "column", gap: theme.spacing["2xs"], alignItems: "flex-end" }}>
                <Button size="small" variant="secondary" onClick={() => onClone(project)}>
                  <FiCopy /> Clone
                </Button>
                <Button size="small" variant="ghost" onClick={() => onDeleteProject(project)}>
                  <FiTrash2 /> Delete
                </Button>
              </div>
            </ProjectHeader>

            {versions.length ? (
              <VersionsGrid>
                {versions.map((version) => {
                  const image = version.current_image_url || version.source_image_url;
                  const sliceLabel = version.slice
                    ? `${version.slice.start_seconds?.toFixed?.(1) || 0}s → ${version.slice.end_seconds?.toFixed?.(1) || 0}s`
                    : "Slice";
                  const remixDisabled = version.state !== "image";
                  const renderDisabled = version.state !== "image";

                  return (
                    <VersionCard key={version.id}>
                      {image ? <Thumb src={image} alt="Version" /> : <CoverPlaceholder />}

                      <Info>
                        <StatusBadge status={version.state}>{version.state}</StatusBadge>
                        <span
                          style={{
                            fontSize: theme.typography.fontSize.xs,
                            color: theme.colors.text.secondary,
                          }}
                        >
                          {sliceLabel}
                        </span>
                      </Info>

                      <Actions>
                        <Button
                          size="small"
                          fullWidth
                          onClick={() => onRemix(version)}
                          disabled={remixDisabled}
                        >
                          {remixDisabled ? "Loading..." : "Remix"}
                        </Button>
                        <Button
                          size="small"
                          fullWidth
                          variant="secondary"
                          onClick={() => onRender(version)}
                          disabled={renderDisabled}
                        >
                          Render
                        </Button>
                        {version.state === "failed" && (
                          <Button size="small" fullWidth variant="secondary" onClick={() => onRetry(version)}>
                            <FiRefreshCcw /> Retry
                          </Button>
                        )}
                        <Button
                          size="small"
                          fullWidth
                          variant="ghost"
                          onClick={() => onDeleteVersion(version)}
                        >
                          <FiTrash2 /> Delete version
                        </Button>
                      </Actions>
                    </VersionCard>
                  );
                })}
              </VersionsGrid>
            ) : (
              <span style={{ color: theme.colors.text.muted }}>No versions yet.</span>
            )}
          </ProjectSection>
        );
      })}
    </ProjectsStack>
  );
}

export default ProjectsPanel;
